// Port of webapp/src/components/centerPanel.tsx — picks the active view,
// computes the visible card set once, and hands it to the four views.

import {ReactElement, useMemo} from 'react'
import {Board} from '../types/board'
import {BoardView} from '../types/boardView'
import {BoardGroup} from '../types/board'
import {Card} from '../types/card'
import {IUser} from '../types/user'
import {useContents} from '../store/contents'
import {computeViewCards} from '../state/viewData'
import Kanban from './kanban/kanban'
import Table from './table/table'
import Gallery from './gallery/gallery'
import Calendar from './calendar/calendar'
import CardDetail from './cardDetail/cardDetail'
import ViewTitle from './viewTitle'
import ViewHeader from './viewHeader/viewHeader'
import TopBar from './topBar/topBar'
import {Icon} from './common/icons'

type Props = {
    board: Board
    views: BoardView[]
    view: BoardView | undefined
    cards: Card[]
    usersById: Record<string, IUser>
    teamId: string
    readonly: boolean
    cardId: string | undefined
    showCard: (cardId: string | undefined) => void
    openSearch: () => void
}

export default function CenterPanel(props: Props): ReactElement {
    const {board, views, view, cards, usersById, teamId, readonly, cardId, showCard, openSearch} = props
    const contents = useContents((state) => state.contents)

    const viewData = useMemo(
        () => computeViewCards(board, view, cards, usersById),
        [board, view, cards, usersById],
    )

    // an ungrouped gallery renders as a single untitled group
    const galleryGroups: BoardGroup[] = viewData.groups.length ? viewData.groups : [{
        option: {id: '', value: '', color: ''},
        cards: viewData.visible,
    }]

    return (
        <div className='CenterPanel flex min-w-0 flex-1 flex-col overflow-hidden'>
            <div className='BoardComponent flex min-h-0 flex-1 flex-col px-8 pt-2'>
                <TopBar onSearch={openSearch}/>
                <div className='flex items-start gap-2'>
                    <ViewTitle board={board} readOnly={readonly}/>
                </div>

                {view && (
                    <ViewHeader
                        board={board}
                        views={views}
                        view={view}
                        cards={cards}
                        visibleCardCount={viewData.visible.length}
                        usersById={usersById}
                        teamId={teamId}
                        readOnly={readonly}
                        onCardClicked={showCard}
                    />
                )}

                {!view && <NoView boardTitle={board.title}/>}

                {view && view.fields.viewType === 'board' && (
                    <Kanban
                        board={board}
                        view={view}
                        groups={viewData.groups}
                        hiddenGroups={viewData.hiddenGroups}
                        usersById={usersById}
                        readOnly={readonly}
                        onCardClicked={showCard}
                    />
                )}

                {view && view.fields.viewType === 'table' && (
                    <Table
                        board={board}
                        view={view}
                        cards={viewData.visible}
                        usersById={usersById}
                        readOnly={readonly}
                        onCardClicked={showCard}
                    />
                )}

                {view && view.fields.viewType === 'gallery' && (
                    <Gallery
                        board={board}
                        view={view}
                        groups={galleryGroups}
                        usersById={usersById}
                        blocksById={contents}
                        readOnly={readonly}
                        onCardClicked={showCard}
                    />
                )}

                {view && view.fields.viewType === 'calendar' && (
                    <Calendar
                        board={board}
                        view={view}
                        cards={viewData.visible}
                        usersById={usersById}
                        readOnly={readonly}
                        onCardClicked={showCard}
                    />
                )}
            </div>

            {cardId && view && (
                <div className='fixed inset-0 z-dialog flex justify-end bg-black/25' onMouseDown={(event) => event.target === event.currentTarget && showCard(undefined)}>
                    <div className='h-full w-full max-w-[900px] bg-main-bg shadow-card'>
                        <CardDetail
                            board={board}
                            view={view}
                            cards={viewData.visible}
                            cardId={cardId}
                            readOnly={readonly}
                            showCard={showCard}
                        />
                    </div>
                </div>
            )}
        </div>
    )
}

function NoView({boardTitle}: {boardTitle: string}): ReactElement {
    return (
        <div className='flex flex-1 flex-col items-center justify-center gap-2 py-20 opacity-70'>
            <Icon name='view-board' size={32}/>
            <div className='text-sm'>“{boardTitle}” has no views. Add one from the view tabs.</div>
        </div>
    )
}
